import { Button, Upload, App } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useCreateTerm, useUpsertTermTranslation } from '@/hooks/useGlossary';

export function ImportCsvButton() {
  const { message } = App.useApp();
  const create = useCreateTerm();
  const upsert = useUpsertTermTranslation();

  async function handle(file: File): Promise<boolean> {
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) throw new Error('CSV needs header + at least 1 row');
      const headers = parseCsvRow(lines[0]);
      const idx = (h: string) => headers.indexOf(h);
      const langCols = headers.filter((h) => !['term', 'preserve_as_is', 'notes'].includes(h));
      let added = 0;
      for (const line of lines.slice(1)) {
        const cols = parseCsvRow(line);
        const term = cols[idx('term')]?.trim();
        if (!term) continue;
        const preserve = cols[idx('preserve_as_is')]?.toLowerCase() === 'true';
        const notes = cols[idx('notes')] || null;
        try {
          const t = await create.mutateAsync({ term, preserve_as_is: preserve, notes });
          for (const lc of langCols) {
            const v = cols[idx(lc)];
            if (v && v.trim()) await upsert.mutateAsync({ termId: t.id, lang: lc, translation: v });
          }
          added++;
        } catch (e) {
          console.warn(`row "${term}" skipped:`, (e as Error).message);
        }
      }
      message.success(`Imported ${added} term(s)`);
    } catch (e) {
      message.error((e as Error).message);
    }
    return false; // prevent default upload
  }

  return (
    <Upload accept=".csv" showUploadList={false} beforeUpload={handle}>
      <Button icon={<UploadOutlined />}>Import CSV</Button>
    </Upload>
  );
}

function parseCsvRow(row: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < row.length; i++) {
    const c = row[i];
    if (c === '"') {
      if (inQ && row[i + 1] === '"') {
        cur += '"';
        i++;
      } else inQ = !inQ;
    } else if (c === ',' && !inQ) {
      out.push(cur);
      cur = '';
    } else cur += c;
  }
  out.push(cur);
  return out;
}
